import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  getResponseErrors,
  IconLoader,
  PermissionEnum,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import { Factor, RiskScoreCalculationType, RiskScoreType } from '../types';
import {
  useUpdateFactorMutation,
  useLazyGetFactorsQuery,
} from '../../store/dictionaries/services';
import GeographicalRiskForm from '../../components/geogrpahical/form';

import cn from './corporate-clients-risk.module.css';

const GeographicalIndividualClientsRisk = () => {
  const { control, handleSubmit, watch, reset } = useForm();

  const corporateClientCalculate = watch('corporateClientCalculate');

  const [weightErrorVisible, setWeightErrorVisible] = useState(false);
  const [errorList, setErrorList] = useState('');

  const {
    amlDictionaries: { activePermissions, factors },
  } = useAppSelector((state) => state);

  const [getFactors] = useLazyGetFactorsQuery();

  const currentFactors = {
    corporate: {
      geography: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_GEOGRAPHY,
      ),
      countryOfIncorporation: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_GEOGRAPHY_CORPORATION,
      ),
      averageRiskScore: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_GEOGRAPHY_DIRECOTRS,
      ),
    },
  };

  const [updateFactors, { isLoading: requestInProgress }] =
    useUpdateFactorMutation();

  const onFormSubmit = handleSubmit((formValues) => {
    let submitAllowed = false;

    if (corporateClientCalculate === RiskScoreCalculationType.WEIGHT) {
      if (
        Number(formValues.corporateClientCountryOfIncorporation) +
          Number(formValues.corporateClientAverageRiskScore) ===
        1
      ) {
        setWeightErrorVisible(false);

        submitAllowed = true;
      } else {
        setWeightErrorVisible(true);

        submitAllowed = false;
      }
    } else if (corporateClientCalculate !== RiskScoreCalculationType.WEIGHT) {
      submitAllowed = true;
    }

    if (submitAllowed) {
      setWeightErrorVisible(false);

      const corporateBody = {
        ...currentFactors.corporate.geography,
        calculationType: formValues.corporateClientCalculate,
      };
      const corporateNationalityBody = {
        ...currentFactors.corporate.countryOfIncorporation,
        weight:
          corporateClientCalculate !== RiskScoreCalculationType.WEIGHT
            ? 0
            : Number(formValues.corporateClientCountryOfIncorporation),
      };
      const corporateResidenceBody = {
        ...currentFactors.corporate.averageRiskScore,
        weight:
          corporateClientCalculate !== RiskScoreCalculationType.WEIGHT
            ? 0
            : Number(formValues.corporateClientAverageRiskScore),
      };

      Promise.all([
        updateFactors({
          id: currentFactors?.corporate?.geography?.id as string,
          body: corporateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.corporate?.countryOfIncorporation?.id as string,
          body: corporateNationalityBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.corporate?.averageRiskScore?.id as string,
          body: corporateResidenceBody as Factor,
        }),
      ]);
    }
  });

  const getFactorsAction = useCallback(async () => {
    const response = (await getFactors()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({ error: response?.error, setErrorList });
    }
  }, []);

  useEffect(() => {
    if (
      currentFactors.corporate.averageRiskScore &&
      currentFactors.corporate.countryOfIncorporation &&
      currentFactors.corporate.geography &&
      !corporateClientCalculate
    ) {
      const body = {
        corporateClientCountryOfIncorporation:
          currentFactors.corporate.countryOfIncorporation.weight,
        corporateClientAverageRiskScore:
          currentFactors.corporate.averageRiskScore.weight,
        corporateClientCalculate:
          currentFactors.corporate.geography.calculationType,
      };

      reset(body);
    }
  }, [currentFactors, corporateClientCalculate, reset]);

  useEffect(() => {
    if (!factors) {
      getFactorsAction();
    }
  }, [factors]);

  useEffect(() => {
    let title = 'Corporate Geographical risk factors';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (!factors) {
    content = (
      <div className={cn.geographicalLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        {!errorList && (
          <GeographicalRiskForm
            control={control}
            onSubmit={onFormSubmit}
            corporateClientCalculate={corporateClientCalculate}
            requestInProgress={requestInProgress}
            updateAllowed={activePermissions?.includes(
              PermissionEnum.SETTINGS_FACTORS_ACTION,
            )}
            weightErrorVisible={weightErrorVisible}
          />
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </>
    );
  }

  return content;
};

export default GeographicalIndividualClientsRisk;
